/**
 * Shell — top-level workspace grid: rail · center · optional inspector.
 *
 * Lays out exactly the columns from the radical artboard:
 *   `64 px / 1fr / 380 px` when an inspector is provided,
 *   `64 px / 1fr` when it is not.
 *
 * The center column is a flex column so children can stack a `<Crown>`,
 * `<Toolband>`, and a scrollable main content region without further wiring.
 *
 * Source: .agents/design/project/artboard-radical.jsx:6-30
 */

import type { ReactNode } from 'react';

interface ShellProps {
  rail: ReactNode;
  inspector?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Shell({ rail, inspector, children, className = '' }: ShellProps) {
  return (
    <div
      className={
        // Cap at viewport height so the inspector's `Body { overflow-auto }` +
        // sticky `Foot` actually engage. With `min-h-full` the grid grew with
        // its tallest child (the list) and the inspector slid off-screen.
        // The same 100dvh cap holds on phone so sticky bottom bars engage.
        //
        // Phone (< sm, Phase 14.1): flex column — AppRail mounts the 56px
        // top bar + drawer instead of the rail (useBreakpoint swap), and the
        // inspector is display:none'd out of layout and the a11y tree
        // (hidden wrapper below; 14.3 replaces it with a route). sm+: the
        // original grid, byte-identical.
        'flex h-[100dvh] flex-col overflow-hidden bg-bg sm:grid ' +
        // Tablet (14.3): inspector narrows to 340px (handoff .t-insp) so the
        // center column keeps ~430px; desktop stays at the original 380px.
        (inspector
          ? 'sm:grid-cols-[64px_minmax(0,1fr)_340px] lg:grid-cols-[64px_minmax(0,1fr)_380px]'
          : 'sm:grid-cols-[64px_minmax(0,1fr)]') +
        ' ' +
        className
      }
    >
      {rail}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">{children}</div>
      {inspector != null && <div className="hidden sm:contents">{inspector}</div>}
    </div>
  );
}
